<script setup lang="ts">
import {computed} from 'vue';
import {useI18n} from 'vue-i18n';
import type {RouteLocationRaw} from 'vue-router';
import ExploreLibraryCard from '@/vue/molecules/recco/explore-library-card.vue';
import ChallengeCard from '@/vue/molecules/recco/challenge-card.vue';
import {useDragToScroll} from '@/ts/composables/pure/use-drag-to-scroll';
import {LibraryContentTypeDTO, type ExploreLibraryItemDTO} from '@/ts/types/dto/recco/library.dto';
import {useViewedContentStore} from '@/ts/store/viewed-content-store';
import {withConsumed} from '@/ts/utils/consumed-overlay';

// A rail shows at most this many items; the rest live behind "View all" (mockup).
const MAX_RAIL_ITEMS = 10;

const props = defineProps<{
  title: string;
  items: ExploreLibraryItemDTO[];
  backLabel: string;
  // Collection destination for "View all". When omitted there is no full-list page,
  // so the rail scrolls all its items rather than capping (no silent truncation).
  viewAllTo?: RouteLocationRaw;
  // Always show "View all" (topic rails: the link replaces the old topics navigation,
  // so it must be visible regardless of the item count).
  alwaysShowViewAll?: boolean;
  // Render challenges as the tall poster cards — only the Challenges section does;
  // elsewhere (the "For you" rail) challenges stay compact, mixed in with the rest.
  bigChallengeCards?: boolean;
}>();

const {t: $t} = useI18n();
const {scrollAreaRef, onMouseDown} = useDragToScroll();
const viewedContent = useViewedContentStore();

const showViewAll = computed<boolean>(() =>
  Boolean(props.viewAllTo) && (Boolean(props.alwaysShowViewAll) || props.items.length > MAX_RAIL_ITEMS));

// Applied here rather than in the card, which stays presentational (see withConsumed).
const railItems = computed<ExploreLibraryItemDTO[]>(() =>
  props.items.map((item) => withConsumed(item, viewedContent.hasViewed(item.content?.id))));

const visibleItems = computed<ExploreLibraryItemDTO[]>(() =>
  showViewAll.value ? railItems.value.slice(0, MAX_RAIL_ITEMS) : railItems.value);

// Rails holding any tall card get a taller track (checked against what actually
// renders, not the full item list).
const hasBigCards = computed<boolean>(() =>
  Boolean(props.bigChallengeCards)
  && visibleItems.value.some((item) => item.contentType === LibraryContentTypeDTO.Challenge));

// Stable per-type key (challenge ids are numeric, content ids are UUIDs).
function itemKey(item: ExploreLibraryItemDTO): string {
  const id = item.content?.id ?? item.challenge?.id ?? '';
  return `${item.contentType}-${id}`;
}
</script>

<template>
  <div class="library-rail">
    <div class="rail-header">
      <h3 class="rail-title">
        {{ title }}
      </h3>
      <router-link v-if="showViewAll" :to="viewAllTo!" class="rail-view-all">
        {{ $t('explore_view_all') }}
      </router-link>
    </div>

    <q-scroll-area ref="scrollAreaRef" class="rail-scroll" :class="{'rail-scroll--big': hasBigCards}">
      <div class="rail-items" @mousedown="onMouseDown">
        <template v-for="item in visibleItems" :key="itemKey(item)">
          <challenge-card
            v-if="bigChallengeCards && item.contentType === LibraryContentTypeDTO.Challenge && item.challenge"
            :id="item.challenge.id"
            :title="item.challenge.title"
            :image-url="item.challenge.imageUrl"
            :challenge-state="item.challenge.challengeState"
            :pct-completion-progress="item.challenge.pctCompletionProgress"
            :recommended="item.recommended"
          />
          <explore-library-card
            v-else
            :item="item"
            :back-label="backLabel"
          />
        </template>
      </div>
    </q-scroll-area>
  </div>
</template>

<style lang="scss" scoped>
.library-rail {
  width: 100%;
  // Inter-section gap: the mockup separates rails by 48px (spacing-7 at desktop).
  margin-top: var(--Spacing-spacing-7);
  display: flex;
  flex-direction: column;
  gap: var(--Spacing-spacing-4);
}

.rail-header {
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: var(--Spacing-spacing-4);
  padding: 0 var(--Spacing-spacing-5);

  @include screen-md {
    padding: 0;
  }
}

.rail-title {
  @include font-h3-bold;
  color: var(--Neutral-UI-neutral-ui-100);
  margin: 0;
}

.rail-view-all {
  @include focus-visible;
  @include font-body-sm-bold;
  flex-shrink: 0;
  color: var(--Neutral-UI-neutral-ui-100);
  text-decoration: none;

  &:hover {
    text-decoration: underline;
  }
}

// Tracks fit their content exactly (6px ring padding + card + 6px) — any slack inside
// the scroll area reads as extra gap between sections (the mockup separates by 48px flat).
.rail-scroll {
  height: $rail-track-height;
  padding: 0 var(--Spacing-spacing-5);

  @include screen-md {
    height: $rail-track-height-md;
    padding: 0;
  }

  // +30px: room under the cards so the horizontal scrollbar doesn't overlap them.
  &--big {
    height: calc(#{$challenge-card-height} + 30px);

    @include screen-md {
      height: calc(#{$challenge-card-height-md} + 30px);
    }

    @include screen-lg {
      height: calc(#{$challenge-card-height-lg} + 30px);
    }

    .rail-items {
      padding-bottom: 24px;
    }
  }
}

.rail-items {
  display: flex;
  gap: var(--Spacing-spacing-4);
  padding: 6px 4px;
  flex-wrap: nowrap;
  cursor: grab;
  user-select: none;
}
</style>
